/**
 * 畫布的幾何：吸附格、卡片大小上下限、自動找位置、內容邊界。
 *
 * 拖進來、點一下放進來、複製一張、匯出圖片 —— 這四件事都要用同一套算法，
 * 所以幾何放在這裡（純函式、可測），不散在元件的事件處理裡。
 */

export type Box = { x: number; y: number; w: number; h: number };

/** 吸附格。拖出來的位置永遠對齊，兩張卡不會差 3px 看起來歪 */
export const GRID = 8;

/* 下限與預設值都取 8px 的倍數：clamp 一次和兩次的結果要一樣 */
export const CARD_MIN_W = 280;
export const CARD_MIN_H = 224;
export const CARD_DEFAULT_W = 520;
export const CARD_DEFAULT_H = 384;

/** 自動找位置時卡片之間的空隙 */
export const GAP = 16;

export const snap = (v: number, grid = GRID) => Math.round(v / grid) * grid;

/** 位置不能是負的（往左上拖出畫布會讓卡片消失） */
export const clampPos = (x: number, y: number) => ({
  x: Math.max(0, snap(x)),
  y: Math.max(0, snap(y)),
});

/** 小於下限的卡片畫不出可讀的圖（軸標籤會先被吃掉），所以縮放有底 */
export const clampSize = (w: number, h: number) => ({
  w: Math.max(CARD_MIN_W, snap(w)),
  h: Math.max(CARD_MIN_H, snap(h)),
});

export const overlaps = (a: Box, b: Box) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * 找一個不壓到別人的位置：由上往下、由左往右掃格子。
 * 掃不到（畫布已經很滿）就以卡片數量做階梯狀偏移 —— 至少不會整疊完全重合。
 */
export function findFreeSpot(
  taken: Box[],
  size: { w: number; h: number },
  canvasWidth: number,
): { x: number; y: number } {
  const stepX = size.w + GAP;
  const stepY = size.h + GAP;
  const cols = Math.max(1, Math.floor((canvasWidth + GAP) / stepX));

  for (let row = 0; row < 60; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const cand: Box = { x: snap(col * stepX), y: snap(row * stepY), ...size };
      if (!taken.some((t) => overlaps(cand, t)))
        return { x: cand.x, y: cand.y };
    }
  }
  const step = snap(GRID * 4);
  return { x: (taken.length % 6) * step, y: (taken.length % 6) * step };
}

/** 畫布上所有卡片的外框 —— 決定畫布要多大，以及匯出要裁多大 */
export function contentBounds(boxes: Box[]) {
  if (boxes.length === 0) {
    return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 };
  }
  const left = Math.min(...boxes.map((b) => b.x));
  const top = Math.min(...boxes.map((b) => b.y));
  const right = Math.max(...boxes.map((b) => b.x + b.w));
  const bottom = Math.max(...boxes.map((b) => b.y + b.h));
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

/** 從工具箱拖圖表到畫布用的 MIME —— 只認自己家的 payload，避免把桌面檔案拖進來也生出一張卡 */
export const CHART_DND_MIME = "application/x-sfc-chart";

export const ALIGN_THRESHOLD = 6;

export type Guide = { axis: "v" | "h"; pos: number; from: number; to: number };

const lines = (start: number, size: number) => [
  start,
  start + size / 2,
  start + size,
];

function nearest(
  movingLines: number[],
  targets: number[],
  threshold: number,
): { delta: number; hits: number[] } | null {
  let best: { delta: number; hits: number[] } | null = null;
  for (const m of movingLines) {
    for (const t of targets) {
      const d = t - m;
      if (Math.abs(d) > threshold) continue;
      if (!best || Math.abs(d) < Math.abs(best.delta))
        best = { delta: d, hits: [] };
    }
  }
  if (!best) return null;
  const delta = best.delta;
  best.hits = [
    ...new Set(
      movingLines
        .map((m) => m + delta)
        .filter((m) => targets.some((t) => Math.abs(t - m) < 0.5)),
    ),
  ];
  return best;
}

function guidesFor(
  axis: "v" | "h",
  hits: number[],
  moving: Box,
  others: Box[],
): Guide[] {
  return hits.map((pos) => {
    const onLine = others.filter((o) =>
      (axis === "v" ? lines(o.x, o.w) : lines(o.y, o.h)).some(
        (l) => Math.abs(l - pos) < 0.5,
      ),
    );
    const span = [moving, ...onLine].flatMap((b) =>
      axis === "v" ? [b.y, b.y + b.h] : [b.x, b.x + b.w],
    );
    return { axis, pos, from: Math.min(...span), to: Math.max(...span) };
  });
}

export function alignMove(
  raw: { x: number; y: number; w: number; h: number },
  others: Box[],
  threshold = ALIGN_THRESHOLD,
): { x: number; y: number; guides: Guide[] } {
  const ax = nearest(
    lines(raw.x, raw.w),
    others.flatMap((o) => lines(o.x, o.w)),
    threshold,
  );
  const ay = nearest(
    lines(raw.y, raw.h),
    others.flatMap((o) => lines(o.y, o.h)),
    threshold,
  );
  const x = Math.max(0, ax ? raw.x + ax.delta : snap(raw.x));
  const y = Math.max(0, ay ? raw.y + ay.delta : snap(raw.y));
  const placed: Box = { x, y, w: raw.w, h: raw.h };
  const guides = [
    ...(ax && x === raw.x + ax.delta
      ? guidesFor("v", ax.hits, placed, others)
      : []),
    ...(ay && y === raw.y + ay.delta
      ? guidesFor("h", ay.hits, placed, others)
      : []),
  ];
  return { x, y, guides };
}

export function alignResize(
  box: Box,
  raw: { w: number; h: number },
  others: Box[],
  threshold = ALIGN_THRESHOLD,
): { w: number; h: number; guides: Guide[] } {
  const ax = nearest(
    [box.x + raw.w],
    others.flatMap((o) => lines(o.x, o.w)),
    threshold,
  );
  const ay = nearest(
    [box.y + raw.h],
    others.flatMap((o) => lines(o.y, o.h)),
    threshold,
  );
  const wantW = ax ? raw.w + ax.delta : snap(raw.w);
  const wantH = ay ? raw.h + ay.delta : snap(raw.h);
  const w = Math.max(CARD_MIN_W, wantW);
  const h = Math.max(CARD_MIN_H, wantH);
  const placed: Box = { x: box.x, y: box.y, w, h };
  const guides = [
    ...(ax && w === wantW ? guidesFor("v", ax.hits, placed, others) : []),
    ...(ay && h === wantH ? guidesFor("h", ay.hits, placed, others) : []),
  ];
  return { w, h, guides };
}
